import { models } from "powerbi-client"
import { fetchJson, fetchBlob, fetchOk, addVerificationToken, fetchString, fetchFileBlob, fetchNumber } from "./datacraft-util"
import { links } from "./links"
import { getSecurityAmender, ISecurityAmender } from "./modules/helpers/authentication-helper"
import { Operation } from "fast-json-patch"
import { raiseFetchResponseError } from "./modules/helpers/response-helpers"
import { DataTableConfiguration, Export, ExportColumn } from "./exports/exportTypes"
import { z } from "zod";
import { smsService } from "./services/sms-services"
import { fetchZ } from "./services/service-helper"
import { workforceService } from "./services/workforce-service"
import { FeatureType } from "./types/shared-types"
import { ReportAction } from "./features/report-actions/report-action-types"


const updatedAtSchema =  z.object({
  updatedAt: z.coerce.date()
});

const patientNoteSchema =  z.object({
  note:z.string(),
  username:z.string(),
  updatedAt: z.coerce.date()
});

const patientNotesSchema = z.object({
  patient: z.nullable(z.optional(patientNoteSchema)).transform(x => x ?? undefined),
  measure: z.nullable(z.optional(patientNoteSchema)).transform(x => x ?? undefined),
  table: z.nullable(z.optional(patientNoteSchema)).transform(x => x ?? undefined)
});

export type PatientNotesType = z.infer<typeof patientNotesSchema>;

type updatedAtType = z.infer<typeof updatedAtSchema>;

const announcementSchema = z.object({
  name: z.string(),
  htmlContent: z.string()
})

export type AnnouncementType = z.infer<typeof announcementSchema>;

export const measuresCreatedSchema =  z.object({
  updatedAt: z.coerce.date(),
  id:z.string()
});

export type ReportMeasureCategoryModel = {pageName:string, name:string, measureCategoryId:number }

export interface IReportInfoResponse {
  displayName:string,
  hasDocumentation: boolean,
  displayBookmarks: boolean,
  canDownload: boolean
  reportKey: number,
  allowsFeedback: boolean,
  clientGroup:string,
  exports: IReportExportResponse[]
  embedSettings: IEmbedSettings,
  measureCategories: ReportMeasureCategoryModel[]
  reportActions?: ReportAction[]
}

export interface IReportExportResponse {
  exportName: string
  pageName: string
  containerName: string
  exportKey: number
  hideInMenu: boolean | undefined
}

export interface IReportNews {
  html: string;
  name: string;
  reportKey: number;
}

export enum ChangeType {
  Added = 'Added',
  Removed = 'Removed',
  Updated = 'Updated'
}

export type ReportFacilityAccessChange = {
  changeType: ChangeType;
  facilityKey: number | null;
  facilityName: string | null;
  decrypt: boolean;
}

export type ReportPermissionChange = {
  changeType: ChangeType;
  reportKey: number;
  reportName: string | null;
  userKey: number;
  userName: string | null;
  facilityChanges: ReportFacilityAccessChange[];
}

export type ReportContext = { reportKey:number, facilityReference?: string }
export type MarkdownModelIdentity = { type:"messaging", messageName:string} 

export interface IEmbedSettings {
  navContentPaneEnabled?: boolean
  // todo caps!!!???
  FilterPaneEnabled?: boolean
  layoutType?:models.LayoutType
}

export interface IEmbedCodeResponse {
  embedToken: string,
  url: string,
  reportId: string,
  expiration: string,
  powerBiType: "Report"|"Dashboard"
  datasets:{[Key: string]: string}
  
}


export interface IMenuQueryView {
  isFacilityManager:boolean
  features:FeatureType[]
  items: INavItem[]
  subscribers: { subscriberKey: number, name: string }[]
  reportFavourites: ReportContext[],
  reports:({ name:string} & ReportContext & { categories:string[] })[]
}

export interface INavItem {
  title: string,
  icon: string,
  key: number,
  facilityReference?: string
  kind: "Link" | "Report"
  url: string,
  subscriberKey?:number
  items?: INavItem[]
}

export type MeasureCategoryListResponse = { name:string, measures:{name:string, measureId:number}[], saved:{ id:string, name:string, username:string, created:Date, updated:Date }[] };

export type PackFileItem = { name: string, packFileId:number }


export type PackSummary = {  packId: number;  name: string; };
export type PackReaderSummary = PackSummary & { isRead:boolean }
export type PackEditorSummary = PackSummary & { isPublished:boolean, groupKey?:number, groupName?:string };
export type PackEditorDetailPractice = {
  reference: string,
  practiceKey:number,
  name: string
  files: PackFileItem[]
  users:{userKey:number, username:string }[]
}
export type PackEditorDetail = PackEditorSummary & {
  folder:string
  everyoneFiles: PackFileItem[]
  practices: PackEditorDetailPractice[],
  rawMarkdown:string
};

export type PackStatsModel = { practiceAccess:PackStatsPracticeModel[], userAccess:{userKey:number, username:string}[],  fileAccess:{ practiceName:string, filename:string,downloadCount:number, usernames:string[]}[], events:{date:string,username:string, eventName:string,description:string}[]}
export type PackStatsPracticeModel = {practiceName:string, opened:boolean}

export type PackUserPractice =  { name:string, files:PackUserPracticeFile[]}
export type PackUserPracticeFile =  { packFileId:number, name:string}
export type PackReaderDetail = PackSummary & {
  packId:number
  name:string
  files:PackUserPracticeFile[]
  practices:PackUserPractice[]
  htmlContent:string

}


export type AuditableUsersResult = {
  practiceId:number,
  practiceName:string,
  users:AuditableUserResult[]
}

export type AuditableUserResult = {
    name:string,
    targetUserKey:Number
}

export type FacilityModel = {
  reference:string,
  name:string
}

export type FacilityDetailModel = { name:string, canCreateUsers: boolean, requestAccess?:string, users:UserModel[],  availableRoles:AvailableRolesModel[]}

export type UserModel = {
  username:string
  userKey:number,
  email:string
}

export type FacilityUserDetailModel = {
  userKey:number,
  username:string
  email:string,
  facilityReference:string
  selectedRoles:number[]
}

export type AvailableRolesModel = {
  groupKey:number
  name:string,
  parentName:string
  explanation: string
}


export type FacilityKeys ={ [name: string]: string }


type BadgeResponse = {badgeName:string, description:string, icon:string}


export type DataTableId = string
export type MeasureCategoryTableId = string 

type ListModel = { 
  name:string,
  measureCategoryName:string,
  configuration:DataTableConfiguration,
  columns:ExportColumn[],
  data:any[]
  metaData:{ [id:string]:{}}
}

const patientsNotesMapSchema = z.record(z.string(),patientNotesSchema)

export type PatientNotes = { patient?:PatientNote, measure?:PatientNote, table?:PatientNote }
export type PatientNote = z.infer<typeof patientNoteSchema>;
export type RawPatientNote = RecursivelyConvertDatesToStrings<PatientNote>
export type MeasureListModel  = {
 patients: z.infer<typeof mapPatentNotesSchema>
} & ListModel
;
export type SavedListModel = MeasureListModel & { updatedAt:Date }

// Transform the object into a Map
const mapPatentNotesSchema = patientsNotesMapSchema.transform((obj) => {
  return new Map(Object.entries(obj));
});

/*
export const downloadReport = async (reportKey: number, exportId: string): Promise<Blob> => await fetchBlob(`/api/powerbi/downloadReport/${reportKey}`, addVerificationToken({
  method: "POST",
  headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify({ exportId })
}));
*/
export const updateCategory = async (key: number, description: string, icon: string) => await fetchOk(`/api/category/${key}`, addVerificationToken({
  method: "PUT", headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify({ description, icon })
}));


export const deleteCategory = async (key: number) => await fetchOk(`/api/category/${key}`, addVerificationToken({
  method: "DELETE", headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
}));

export const createCategory = async (description: string, icon: string): Promise<{ key: number }> => await fetchJson(`/api/category/`, addVerificationToken({
  method: "POST", headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify({ description, icon })
}));



export const updateGroupUser = async (groupUserKey: number, drillDown: Boolean) => await fetchOk(`/api/groupUser/${groupUserKey}`, addVerificationToken({
  method: "PUT", headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify({ drillDown })
}));

const securityAmender:ISecurityAmender = getSecurityAmender();
export const services = {
  announcements:{
    previewUsers: async (firstGroupKey: number, secondGroupKey?: number) => {
      const queryParams = `?firstGroupKey=${firstGroupKey}${secondGroupKey ? `&secondGroupKey=${secondGroupKey}` : ""}`;
      return await fetchJson<string[]>(`/api/announcements/preview-users${queryParams}`, await securityAmender.AppendSecurityToRequest({}));
    },
    getAnnouncements: async (filterId?: number, filter?: string) => {
      const filterIdParam = filterId ? `?filterId=${filterId}` : "";
      const filterParam = filter ? `?filter=${filter}` : "";
      return await fetchZ(z.array(announcementSchema), `/api/announcements${filterIdParam}${filterParam}`, await securityAmender.AppendSecurityToRequest({}));
    }
  },
  navigation:{
    menu: async () => await fetchJson<IMenuQueryView>('/api/navigation/menu', await securityAmender.AppendSecurityToRequest({}))
  },
  sms:smsService,
  //tables:{
    //NOTE: this is all turned Disabled
    // list:async () => await fetchJson<{ id:DataTableId, name:string, createdAt:string }[]>(`/api/data-tables/`, await securityAmender.AppendSecurityToRequest({})),
    // create:async(name:string,configuration:DataTableConfiguration, columns: ExportColumn[], data:any[])=> await fetchJson<{id:DataTableId}>("/api/data-tables/create", await postBody({name, configuration, columns, data})),
    // get:async(dataTableId:DataTableId) => await fetchJson<ListModel>(`/api/data-tables/${dataTableId}`, await securityAmender.AppendSecurityToRequest({})),
    // update:async(dataTableId:DataTableId, data:any[] ) => await fetchOk(`/api/data-tables/${dataTableId}`, await postBody({ data })),
    // delete:async(dataTableId:DataTableId) => await fetchOk(`/api/data-tables/${dataTableId}`, await deleteBody({}))
  //},
  content:{
    getContentPath:async(contentPath:string) => await fetchJson<{title:string, icon?:string, htmlContent:string}>(`/api/content/page/${contentPath}`,  await securityAmender.AppendSecurityToRequest({}))
  },
  measures:{
    createActive:async(name:string, measureCategoryId:number, facility:string, configuration:DataTableConfiguration, columns: ExportColumn[], data:any[])=> await fetchZ(measuresCreatedSchema,`/api/measures/create-active/${facility}/${measureCategoryId}`, await postBody({name,configuration, columns, data})),
    getLatest:async(measureCategoryId:number, facility:string, measureId?:number) => {
      const measureIdPart = measureId ? `?measure=${measureId}` : "";
      var response = await fetchJson<RecursivelyConvertDatesToStrings<MeasureListModel>>(`/api/measures/latest/${facility}/${measureCategoryId}${measureIdPart}`, await securityAmender.AppendSecurityToRequest({}))
      return  { ...response, "patients" : mapPatentNotesSchema.parse(response.patients) }
    },
    updateTable:async(measureCategoryTableId:MeasureCategoryTableId, lastModified:Date, data:any[] ): Promise<updatedAtType> =>  { 
      let createPost = await postBody({ data });
      createPost = {...createPost, headers: {...createPost.headers, "If-Unmodified-Since":lastModified.toUTCString()}}
      return updatedAtSchema.parse(await fetchJson<any>(`/api/measures/table/${measureCategoryTableId}`, createPost)) 
    },
    openTable:async(measureCategoryTableId:MeasureCategoryTableId):Promise<SavedListModel> =>{
      const  response = await fetchJson<RecursivelyConvertDatesToStrings<SavedListModel>>(`/api/measures/table/${measureCategoryTableId}`,await securityAmender.AppendSecurityToRequest({}))
      return { ...response, "updatedAt":new Date(response.updatedAt),  "patients" : mapPatentNotesSchema.parse(response.patients)}
    },
    list:async (measureCategoryId:number, facilityReference:string):Promise<MeasureCategoryListResponse> => {
      var init = await fetchJson<RecursivelyConvertDatesToStrings<MeasureCategoryListResponse>>(links.api.measures.list(measureCategoryId, facilityReference), await securityAmender.AppendSecurityToRequest({}))
      return {...init, saved: init.saved.map(i => ({...i, "created": new Date(i.created), "updated":new Date(i.updated)}))};
    },
    updateNotes:async(facilityReference:string, patientId:string, measureCategoryId:number, reportMeasureCategoryTableId?:string, patient?:string, measure?:string, table?:string) => {
      var tablePart = reportMeasureCategoryTableId != null ? `?measureCategoryDataTableId=${reportMeasureCategoryTableId}` : ""
      return await fetchZ(patientNotesSchema,`/api/measures/notes/${facilityReference}/${measureCategoryId}/update/${patientId}/${tablePart}`, await postBody({ patient, measure, table }))
    }
  },
  dashboard:{
    landingContent: async () => await fetchString('/LandingContent', await securityAmender.AppendSecurityToRequest({}))
  },
  workforce:workforceService,
  report: {
    info: async (reportKey: number) => await fetchJson<IReportInfoResponse>(`/api/report/${reportKey}/info`, await securityAmender.AppendSecurityToRequest({})),
    news: async (preview: boolean, subscriberKey?: number) => {
      const subscriberKeyPart = subscriberKey ? `&subscriberKey=${subscriberKey}` : "";
      return fetchJson<IReportNews[]>(`/api/report-news/list?preview=${preview}${subscriberKeyPart}`, await securityAmender.AppendSecurityToRequest({}))
    },
    updateFavourite: async (reportContext:ReportContext, enable:boolean) =>  await fetchOk(`/api/reports/update-favourite`, await postBody({ ...reportContext, enable})),
    dismissNews: async (reportKey: number) => await fetchOk(`/api/report-news/dismiss`, await postBody({reportKey})),
    documentationContent:async (reportKey: number) => await fetchJson<{content:string, useShadowDom:boolean}>(`/api/report/${reportKey}/documentation`, await securityAmender.AppendSecurityToRequest({})),
    embedToken: async (reportKey: number, username?: string, facilityReference?:string, isRefresh:boolean = false) => {  
      const userPart = username ? `&username=${username}` :"";
      const facilityPart = facilityReference ? `&facility=${facilityReference}`: "";
      return fetchJson<IEmbedCodeResponse>(`/api/powerbi/embedcode/${reportKey}/?isRefresh=${isRefresh}${userPart}${facilityPart}`,await securityAmender.AppendSecurityToRequest({}))
    },
    permissionChanges: async (reportKey?: number, subscriberReference?: string) => {
      const queryParam = reportKey ? `?reportKey=${reportKey}` : subscriberReference ? `?subscriberReference=${subscriberReference}` : "?subscribersWithReportEditPermission=true";
      return fetchJson<ReportPermissionChange[]>(`/api/report-permission-changes${queryParam}`, await securityAmender.AppendSecurityToRequest({}))
    },
    reportActions: {
      upsert: async (reportAction: ReportAction) =>
        await fetchJson<ReportAction>(`/api/report/${reportAction.reportKey}/actions/upsert`, await postBody(reportAction)),
      delete: async (reportKey: number, reportActionKey: number) =>
        await fetchOk(`/api/report/${reportKey}/action/${reportActionKey}/delete`, await deleteBody({}))
    },
    exports: {
      detail:async (reportKey:number, exportKey:number, facilityReference?:string) => {
        let facilityReferencePart = "";
        if(facilityReference)
        {
          facilityReferencePart = `?facility=${facilityReference}`;
        }
       return await fetchJson<{ decryptionKeys: FacilityKeys, export:Export}>(`/api/export/report/${reportKey}/export/${exportKey}/detail${facilityReferencePart}`, await securityAmender.AppendSecurityToRequest({}))
      },
      update:async (reportKey:number, exportKey:number, exportItem:Export) => await fetchOk(`/api/export/report/${reportKey}/export/${exportKey}/update-export2`, await postBody(exportItem)),
      create:async (reportKey:number, exportItem:Export) => await fetchNumber(`/api/export/report/${reportKey}/create-export2`, await postBody(exportItem))
    },
    download:{
      requestDownload : async (reportKey: number, state: string, pageName: string, facilityReference?:string, username?:string ): Promise<{ exportId: string }> => await fetchJson(`/api/powerbi/requestReportDownload/${reportKey}`, await postBody({ state, pageName, username, facilityReference })),
      downloadStatus: async (reportKey: number, exportId: string): Promise<{ state: " Running" | "Succeeded" | "Failed", complete: boolean, percentComplete: number }> => await fetchJson(`/api/powerbi/reportDownloadStatus/${reportKey}`, await postBody({ exportId })),
      retrieveDownload: async (reportKey: number, exportId: string): Promise<Blob> => await fetchBlob(`/api/powerbi/downloadReport/${reportKey}`, await postBody({ exportId }))
    }
  },
  facilities:{
    auditableUsers:async() => await fetchJson<AuditableUsersResult>(`/api/facilities/auditable-users`, await securityAmender.AppendSecurityToRequest({})),
    userDetail:async(facilityReference:string, userKey:number) => await fetchJson<FacilityUserDetailModel>(`/api/facilities/${facilityReference}/users/${userKey}`, await securityAmender.AppendSecurityToRequest({})).catch(raiseFetchResponseError),
    createFacilityUser: async(facilityReference:string, email:string, firstName:string, lastName:string, sendWelcomeEmail:boolean, roleGroups:number[]) => await fetchJson<{ userKey:number }>(`/api/facilities/${facilityReference}/users/create`, await postBody({email,firstName,lastName, sendWelcomeEmail,roleGroups})).catch(raiseFetchResponseError),
    updateFacilityUser: async(facilityReference:string, userKey:number, roleGroups:number[]) => await fetchOk(`/api/facilities/${facilityReference}/users/${userKey}/update`, await postBody({ roleGroups:roleGroups})).catch(raiseFetchResponseError),
    sendWelcome: async(facilityReference:string, userKey:number) => await fetchOk(`/api/facilities/${facilityReference}/users/${userKey}/send-welcome`, await postBody({})).catch(raiseFetchResponseError),
    removeUserFromFacility:  async(facilityReference:string, userKey:number) => await fetchOk(`/api/facilities/${facilityReference}/users/${userKey}`, await deleteBody({})).catch(raiseFetchResponseError),
    manageableFacilities: async (): Promise<FacilityModel[]> => await fetchJson<FacilityModel[]>(`/api/facilities/manageable-facilities`, await securityAmender.AppendSecurityToRequest({})).catch(raiseFetchResponseError),
    detail:async(facilityReference:string) => await fetchJson<FacilityDetailModel>(`/api/facilities/${facilityReference}/detail`, await securityAmender.AppendSecurityToRequest({})).catch(raiseFetchResponseError),
  },
  management: {
    workspaces: async (): Promise<{ name: string, workspaceKey: number }[]> => await fetchJson(`/api/management/workspace`, {
      method: "GET", headers: {
        'Accept': 'application/json; charset=utf-8',
        'Content-Type': 'application/json;charset=UTF-8'
      }
    }),
    workspace: {
      powerBiReports: async (powerBiGroupKey: number): Promise<[{ name: string, id: string }]> => await fetchJson(`/api/management/workspace/${powerBiGroupKey}/powerbi-reports`, {
        method: "GET", headers: {
          'Accept': 'application/json; charset=utf-8',
          'Content-Type': 'application/json;charset=UTF-8'
        }
      }),

    },
    markdown:{
      preview: async (markdown:string, abortSignal?:AbortSignal) => await fetchJson<{markup:string}>(links.markdown.preview(), await postBody({ markdown }, abortSignal)),
      publish:async (entity:MarkdownModelIdentity, markdown:string) => {
        return await fetchOk(`/api/markdown/publish-content`, await postBody({ entity:entity, markdown }))
      },
      saveVersion:async (entity:MarkdownModelIdentity, markdown:string) => {
        return await fetchOk(`/api/markdown/save-version`, await postBody({ entity:entity, markdown }))
      },
      published:async (entity:MarkdownModelIdentity) => {
        return await fetchJson<{markdown:string}>(`/api/markdown/published-markdown`, await postBody(entity))
      },
    },
    listSubscribers: async (): Promise<{ subscriberKey: number, name: string }[]> => await fetchJson(`/api/management/subscribers/list`),
    createReport: async (subscriberKey: number, name: string, workspaceKey: number, reportId: string): Promise<number> => await fetchJson(`/api/management/${subscriberKey}/create-report/`,
      addVerificationToken({
        method: "POST",
        headers: {
          'Accept': 'application/json; charset=utf-8',
          'Content-Type': 'application/json;charset=UTF-8'
        },
        body: JSON.stringify({ name, workspaceKey, reportId })
      })
    ),
    exports: {
      delete: async (exportKey: number): Promise<void> => await fetchOk(`/api/export/${exportKey}`, addVerificationToken({
        method: "DELETE", headers: {
          'Accept': 'application/json; charset=utf-8',
          'Content-Type': 'application/json;charset=UTF-8'
        }
      }))
    }
  },
  subscribers: {

    createReport: async (subscriberKey: number, data: { name: string, reportId: string, enableBookmarks: boolean, enableDownload: boolean, categories: number[] }): Promise<number> => await fetchJson(`/api/subscribers/${subscriberKey}/create-report/`,
      addVerificationToken({
        method: "POST",
        headers: {
          'Accept': 'application/json; charset=utf-8',
          'Content-Type': 'application/json;charset=UTF-8'
        },
        body: JSON.stringify(data)
      })
    ),

    listPowerBiReports: async (subscriberKey: number): Promise<{ id: string, name: string, powerBiType:"Report"|"Dashboard" }[]> => await fetchJson(`/api/subscribers/${subscriberKey}/powerbi-reports`),
    requestPermissionPublish: async():Promise<void> => await fetchOk("/api/manage-subscriber/request-permission-publish", await postBody({}))
  },
  categories: {
    list: async (): Promise<{ key: number, name: string, icon: string }[]> => await fetchJson(`/api/category/`)
  },
  users: {
    checkSignedIn: async() => await fetchOk(`/api/user/check-signed-in`),
    ping: async (): Promise<void> => await fetchOk(`/api/user/ping`),
    sendFeedback:async (url:string, message:string, reportKey?:number) => await fetchOk(`/api/user/send-feedback`, await postBody({ url, message, reportKey })),
    colleagueAuditUser:async (targetUser:Number, facilityKey:number,  isCurrentEmployee:boolean) => await fetchOk(`/api/user/${targetUser}/practice/${facilityKey}/employedstatus`, await postBody(isCurrentEmployee))
  },
  badge:{
    current: async (): Promise<{ recent:BadgeResponse[], awarded:BadgeResponse[],available: BadgeResponse[] }> => await fetchJson(`/api/badge/current`,await securityAmender.AppendSecurityToRequest({}))
  },
  events:{
    raise:async(action:"TeamsOpened"|"BadgesOpened"|"TutorialsOpened"): Promise<void> => await fetchOk(`/api/events`, await postBody({actionType:action}))
  },
  groups:{
    list: async (subscriberKey:number, userGroupsOnly?:boolean): Promise<{name:string,groupKey:number}[]> => { 
      let userPart = "";
      if(userGroupsOnly != null)
      {
        userPart +=`&isUserGroup=${userGroupsOnly}`;
      }
      return await fetchJson(`/api/group/list?subscriberKey=${subscriberKey}${userPart}`,await securityAmender.AppendSecurityToRequest({}))
    },
  },
  packs:{
    list: async (): Promise<PackReaderSummary[]> => await fetchJson(`/api/pack/list`,await securityAmender.AppendSecurityToRequest({})),
    packUserDetail: async (packId:number) =>{
      return await fetchJson<PackReaderDetail>(`/api/pack/${packId}/user`, await securityAmender.AppendSecurityToRequest({}))
    },
    download: async(packFileId:number)  => {
      return await fetchFileBlob(`/api/packs/files/${packFileId}/download`,await securityAmender.AppendSecurityToRequest({}))
      //return await fetchFileBlob(`/api/pack/pack-file/${packFileId}/download`,await securityAmender.AppendSecurityToRequest({}))
    },

    editor:{
      pack:(packId:number) => ({
        delete: async () =>  await fetchOk(`/api/pack/editor/${packId}`, await deleteBody({})),
        edit: async (pack:PackEditorDetail) =>await fetchJson<PackEditorDetail>(`/api/pack/editor/update-pack`, await putBody(pack)),
        patch: async (operations:Operation[]) =>await fetchOk(`/api/pack/editor/patch-pack-summary/${packId}`, await postBody(operations)),
        detail: async () => await fetchJson<PackEditorDetail>(`/api/pack/editor/${packId}/detail`, {}),
        stats: async() => await fetchJson<PackStatsModel>(`/api/pack/editor/${packId}/stats`, await securityAmender.AppendSecurityToRequest({})),
        upload: async (formData:FormData) => {
          return await fetchOk(`/api/pack/editor/${packId}/upload`, await securityAmender.AppendSecurityToRequest({
                method: "POST",
                body: formData
              }));
        }
      }),
      createPack: async (subscriberKey:number, name:string, groupKey?:number) =>{
        return await fetchJson<PackEditorDetail>(`/api/pack/editor/create-pack`, await postBody({subscriberKey, name,groupKey}))
      },
      list: async (subscriberKey:number) =>{
        return await fetchJson<PackEditorSummary[]>(`/api/pack/editor/list/${subscriberKey}`, {})
      },
      deletePackFile: async(packFileId:number)  => {
        return await fetchOk(`/api/pack/editor/pack-file/${packFileId}`,await deleteBody({}))
      },
      sendTestEmail: async(packId:number, subject:string, body: string) =>{
        return await fetchOk(`/api/pack/editor/${packId}/test-email`,await postBody({ subject, body }))
      },
      sendEmail: async(packId:number, subject:string, body: string) =>{
        return await fetchOk(`/api/pack/editor/${packId}/send-email`,await postBody({ subject, body }))
      } 
    }
  }

}


const deleteBody = (data: any) => securityAmender.AppendSecurityToRequest({
  method: "DELETE",
  headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify(data)
});


const postBody = (data: any, abortSignal?:AbortSignal) => securityAmender.AppendSecurityToRequest({
  method: "POST",
  headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify(data),
  signal:abortSignal
});




const putBody = (data: any) => securityAmender.AppendSecurityToRequest({
  method: "PUT",
  headers: {
    'Accept': 'application/json; charset=utf-8',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  body: JSON.stringify(data)
});

